import React, { useState, useEffect } from 'react';
import Chip from '@solar/common/Chip/Chip';
import MobileSRPDrawer from '@components/SearchExperiment/SRPFilters/MobileSRPDrawer';
import {
  AllFiltersDrawer,
  PoolFeaturesDrawer,
  SpaServicesDrawer,
  SpaFeaturesDrawer,
  AmenitiesDrawer,
  HotelStarsDrawer,
  VibesDrawer,
  GuestRatingDrawer,
} from '@components/SearchExperiment/SRPFilters/MobileSRPDrawerFilters';
import { FilterIcon, ChevronDownIcon, CloseIcon } from '@resortpass/solar-icons';
import { SearchState } from '@customTypes/search';
import {
  useSearchContext,
  AmenitiesFriendlyNames,
  HotelStarsFriendlyNames,
  GuestRatingFriendlyNames,
  VibesFriendlyNames,
  PoolFeatures,
  PoolProductTypes,
  SpaFeatures,
  SpaProductTypes,
  ProductCategorySpecificFilters,
} from '@context/SearchContext2025';
import cn from '@helpers/className';
import { useDrawer } from '@context/DrawerContext';
import styles from './MobileSRPFilters.module.css';

/**
 * MobileSRPChip component is a reusable component that displays a chip with a title
 * and an example of the applied filters.
 * It is used to represent various types of filters in the mobile search results page.
 */

type MobileSRPChipProps = {
  defaultTitle: string;
  applied?: Array<string>;
  onClick?: React.MouseEventHandler;
  onDelete?: React.MouseEventHandler;
};
function MobileSRPChip({
  defaultTitle,
  applied = [],
  onClick = () => {},
  onDelete = () => {},
}: MobileSRPChipProps) {
  return (
    <Chip
      className="border border-solar-secondary cursor-pointer"
      backgroundClass={
        applied.length > 0 ? 'bg-solar-surface-secondary' : 'bg-solar-surface-secondary-default'
      }
      onClick={onClick}
      text={
        applied.length > 0
          ? `${applied[0]}
              ${applied.length > 1 ? ` + ${applied.length - 1}` : ''}`
          : defaultTitle
      }
      rightIcon={
        applied.length > 0 ? (
          <CloseIcon width={16} height={16} onClick={onDelete} />
        ) : (
          <ChevronDownIcon width={16} height={16} />
        )
      }
    />
  );
}

type MobileSRPFiltersProps = {
  className?: string;
};

export default function MobileSRPFilters({ className = '' }: MobileSRPFiltersProps) {
  const searchContext = useSearchContext();
  const { searchParams: searchState } = searchContext;
  const { product_category: productCategory } = searchState;
  const { filters } = searchState;
  const { openDrawerWithComponent } = useDrawer();
  const [fireSearch, setFireSearch] = useState(false);

  // autosearch when fireSearch is set to true
  useEffect(() => {
    if (fireSearch) {
      searchContext.search(1);
      setFireSearch(false);
    }
  }, [fireSearch, searchContext]);

  // -------------------------------------------------------------------
  // Computed Filter Data and Processing -------------------------------
  // -------------------------------------------------------------------
  const shouldShowVibes = productCategory === '' || productCategory === 'pool';
  const shouldShowPoolFeatures = ['', 'pool', 'day room'].includes(productCategory);
  const shouldShowSpaFeatures = ['spa'].includes(productCategory);
  const appliedSRPFiltersCount = [
    ...(filters.only_available ? [true] : []),
    ...Object.entries(HotelStarsFriendlyNames).filter(
      ([key]) => filters.hotel_stars[key as keyof SearchState['filters']['hotel_stars']],
    ),
    ...Object.entries(AmenitiesFriendlyNames).filter(
      ([key]) => filters.amenities[key as keyof SearchState['filters']['amenities']],
    ),
    ...Object.entries(VibesFriendlyNames).filter(
      ([key]) => filters.vibes[key as keyof SearchState['filters']['vibes']],
    ),
    ...Object.entries(GuestRatingFriendlyNames).filter(
      ([key]) => filters.guest_ratings[key as keyof SearchState['filters']['guest_ratings']],
    ),
    ...Object.entries(PoolProductTypes).filter(([, value]) =>
      filters.product_types.includes(value)),
    ...Object.entries(SpaProductTypes).filter(([, value]) => filters.product_types.includes(value)),
  ].length;

  // Pool Features Aggregation
  const appliedPoolFeatures: Array<string> = Object.entries(filters.amenities)
    .filter(
      ([key, applied]) => PoolFeatures.includes(key as keyof typeof filters.amenities) && applied,
    )
    .map(([key]) => AmenitiesFriendlyNames[key as keyof typeof AmenitiesFriendlyNames])
    .concat(
      Object.entries(PoolProductTypes)
        .filter(([, productTypeId]) => filters.product_types.includes(productTypeId))
        .map(([key]) => key),
    );

  const appliedSpaServices: Array<string> = Object.entries(SpaProductTypes)
    .filter(([, productTypeId]) => filters.product_types.includes(productTypeId))
    .map(([key]) => key);

  const appliedSpaFeatures: Array<string> = Object.entries(filters.amenities)
    .filter(
      ([key, applied]) => SpaFeatures.includes(key as keyof typeof filters.amenities) && applied,
    )
    .map(([key]) => AmenitiesFriendlyNames[key as keyof typeof AmenitiesFriendlyNames]);

  const appliedAmenities: Array<string> = Object.entries(filters.amenities)
    .filter(
      ([key, applied]) =>
        !ProductCategorySpecificFilters.has(key as keyof typeof filters.amenities) && applied,
    )
    .map(([key]) => AmenitiesFriendlyNames[key as keyof typeof AmenitiesFriendlyNames]);

  const appliedGuestRatings: Array<string> = Object.entries(filters.guest_ratings)
    .filter(([, applied]) => applied)
    .map(([key]) => GuestRatingFriendlyNames[key as keyof typeof GuestRatingFriendlyNames]);

  const appliedHotelStars: Array<string> = Object.entries(filters.hotel_stars)
    .filter(([, applied]) => applied)
    .map(([key]) => HotelStarsFriendlyNames[key as keyof typeof HotelStarsFriendlyNames]);

  const appliedVibes: Array<string> = Object.entries(filters.vibes)
    .filter(([, applied]) => applied)
    .map(([key]) => VibesFriendlyNames[key as keyof typeof VibesFriendlyNames]);

  const openFilterDrawer = (name: string, component: React.ReactElement) =>
    openDrawerWithComponent(<MobileSRPDrawer title={name}>{component}</MobileSRPDrawer>, name);

  // Interaction Handlers
  const clearPoolFeatures = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    searchContext.setAmenities({
      ...PoolFeatures.reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {} as SearchState['filters']['amenities']),
    });

    Object.values(PoolProductTypes).forEach((productTypeId) => {
      searchContext.removeProductType(productTypeId);
    });

    setFireSearch(true);
  };

  const clearSpaFeatures = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    searchContext.setAmenities({
      ...SpaFeatures.reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {} as SearchState['filters']['amenities']),
    });
    setFireSearch(true);
  };

  const clearSpaServices = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    Object.values(SpaProductTypes).forEach((productTypeId) => {
      searchContext.removeProductType(productTypeId);
    });

    setFireSearch(true);
  };

  const clearGuestRatings = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    searchContext.setGuestRatings({
      ...Object.keys(filters.guest_ratings).reduce((acc, key) => {
        acc[key as keyof SearchState['filters']['guest_ratings']] = false;
        return acc;
      }, {} as SearchState['filters']['guest_ratings']),
    });

    setFireSearch(true);
  };

  const clearAmenities = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const amenities = Object.keys(filters.amenities)
      .filter((key) => !ProductCategorySpecificFilters.has(key as keyof typeof filters.amenities))
      .reduce((acc, amenity) => {
        acc[amenity as keyof SearchState['filters']['amenities']] = false;
        return acc;
      }, {} as SearchState['filters']['amenities']);

    searchContext.setAmenities({
      ...amenities,
    });

    setFireSearch(true);
  };

  const clearHotelClass = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    searchContext.setHotelStars({
      ...Object.keys(filters.hotel_stars).reduce((acc, key) => {
        acc[key as keyof SearchState['filters']['hotel_stars']] = false;
        return acc;
      }, {} as SearchState['filters']['hotel_stars']),
    });

    setFireSearch(true);
  };

  const clearVibes = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    searchContext.setVibes({
      ...Object.keys(filters.vibes).reduce((acc, key) => {
        acc[key as keyof SearchState['filters']['vibes']] = false;
        return acc;
      }, {} as SearchState['filters']['vibes']),
    });
    setFireSearch(true);
  };

  return (
    <div
      className={cn({
        'flex flex-nowrap py-spacing-md pl-spacing-md': true,
        [className]: !!className,
      })}
    >
      <div className="flex-none z-100 relative">
        <Chip
          className="border border-solar-secondary cursor-pointer"
          backgroundClass="bg-solar-button-secondary-default"
          text="Filter"
          onClick={() => openFilterDrawer('Filters', <AllFiltersDrawer />)}
          rightIcon={(
            <>
              {appliedSRPFiltersCount > 0 && (
                <div className="text-center text-11 text-white rounded-full bg-solar-surface-brand-shadow w-4 h-4">
                  {appliedSRPFiltersCount}
                </div>
              )}
              <FilterIcon width={16} height={16} />
            </>
          )}
        />
      </div>

      <div
        className={`flex flex-nowrap overflow-x-auto min-w-0 no-scrollbar px-spacing-sm ${styles.maskChips}`}
      >
        <div className="flex flex-nowrap gap-x-spacing-sm">
          <MobileSRPChip
            defaultTitle="Hotel Class"
            applied={appliedHotelStars}
            onClick={() => openFilterDrawer('Hotel Class', <HotelStarsDrawer />)}
            onDelete={clearHotelClass}
          />

          {shouldShowPoolFeatures && (
            <MobileSRPChip
              defaultTitle="Pool Features"
              applied={appliedPoolFeatures}
              onClick={() => openFilterDrawer('Pool Features', <PoolFeaturesDrawer />)}
              onDelete={clearPoolFeatures}
            />
          )}

          {shouldShowSpaFeatures && (
            <>
              <MobileSRPChip
                defaultTitle="Spa Services"
                applied={appliedSpaServices}
                onClick={() => openFilterDrawer('Spa Services', <SpaServicesDrawer />)}
                onDelete={clearSpaServices}
              />
              <MobileSRPChip
                defaultTitle="Spa Features"
                applied={appliedSpaFeatures}
                onClick={() => openFilterDrawer('Spa Features', <SpaFeaturesDrawer />)}
                onDelete={clearSpaFeatures}
              />
            </>
          )}

          <MobileSRPChip
            defaultTitle="Amenities"
            applied={appliedAmenities}
            onClick={() => openFilterDrawer('Amenities', <AmenitiesDrawer />)}
            onDelete={clearAmenities}
          />

          <MobileSRPChip
            defaultTitle="Guest Rating"
            applied={appliedGuestRatings}
            onClick={() => openFilterDrawer('Guest Rating', <GuestRatingDrawer />)}
            onDelete={clearGuestRatings}
          />

          {shouldShowVibes && (
            <MobileSRPChip
              defaultTitle="Vibes"
              applied={appliedVibes}
              onClick={() => openFilterDrawer('Vibes', <VibesDrawer />)}
              onDelete={clearVibes}
            />
          )}
        </div>
      </div>
    </div>
  );
}
